export function splitExtension(filename) {
  const name = String(filename || '');
  const lastDot = name.lastIndexOf('.');
  // Leading-dot only ('.gitignore'), no extension
  if (lastDot <= 0) return { stem: name, ext: '' };
  // Trailing dot ('weird.'), no extension
  if (lastDot === name.length - 1) return { stem: name, ext: '' };
  return { stem: name.slice(0, lastDot), ext: name.slice(lastDot) };
}

// Hard-lock a rename to the original file's extension. The extension can never
// be removed or changed: if the draft already ends with the original extension
// we keep it (normalizing its casing back to the original); otherwise we append
// the original extension. Folders / extensionless originals are returned as-is.
export function enforceExtension(draftName, originalName) {
  const draft = String(draftName || '').trim();
  const { ext } = splitExtension(originalName);
  if (!ext || !draft) return draft;
  if (draft.toLowerCase().endsWith(ext.toLowerCase())) {
    return draft.slice(0, draft.length - ext.length) + ext;
  }
  return draft + ext;
}

export function normalizeNameForCompare(name) {
  return String(name || '').trim().toLowerCase();
}

export function pickAvailableName(baseName, existingNames) {
  const normalizedSet = new Set();
  for (const n of existingNames || []) {
    normalizedSet.add(normalizeNameForCompare(n));
  }
  if (!normalizedSet.has(normalizeNameForCompare(baseName))) {
    return baseName;
  }
  const { stem, ext } = splitExtension(baseName);
  for (let n = 1; n < 10000; n++) {
    const candidate = `${stem} (${n})${ext}`;
    if (!normalizedSet.has(normalizeNameForCompare(candidate))) {
      return candidate;
    }
  }
  // Pathological case — fall back to a timestamp suffix
  return `${stem} (${Date.now()})${ext}`;
}

export function findNameConflicts(incomingFiles, existingItems) {
  const incoming = Array.isArray(incomingFiles) ? incomingFiles : [];
  const existing = Array.isArray(existingItems) ? existingItems : [];

  // Map from normalized name → array of existing-file ids
  const byName = new Map();
  for (const item of existing) {
    const norm = normalizeNameForCompare(item?.name);
    if (!norm) continue;
    const id = item?.id || item?.googleDriveId || null;
    if (!byName.has(norm)) byName.set(norm, []);
    if (id) byName.get(norm).push(id);
  }

  const conflicts = [];
  // Track names already seen in the incoming batch — second occurrence
  // of the same name is itself a conflict (same-batch duplicate).
  const seenInBatch = new Set();
  for (const file of incoming) {
    const norm = normalizeNameForCompare(file?.name);
    if (!norm) continue;
    if (byName.has(norm) || seenInBatch.has(norm)) {
      conflicts.push({
        name: file.name,
        existingDriveFileIds: byName.get(norm) ? [...byName.get(norm)] : [],
      });
    }
    seenInBatch.add(norm);
  }
  return conflicts;
}
